import React, { useEffect, useRef } from "react";
import { View, Text, StyleSheet, ScrollView } from "react-native";
import type { TranscriptSegment, SupportedLanguage } from "@nativecall/shared";

export type PartialTranscript = {
  speaker: "caller" | "callee";
  sourceLanguage: SupportedLanguage;
  sourceText: string;
  startedAtMs: number;
};

type Props = {
  segments: TranscriptSegment[];
  partials: Record<"caller" | "callee", PartialTranscript | null>;
  youLabel: string;
  themLabel: string;
  waitingLabel: string;
  lowConfidenceIds: Set<string>;
  title: string;
};

const MAX_SEGMENTS = 20;

function formatTs(ms: number): string {
  const d = new Date(ms);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

export function CallTranscriptChat({
  segments,
  partials,
  youLabel,
  themLabel,
  waitingLabel,
  lowConfidenceIds,
  title,
}: Props) {
  const visible = segments.slice(-MAX_SEGMENTS);
  const scrollRef = useRef<ScrollView>(null);

  const latestCommittedAt = (speaker: "caller" | "callee"): number => {
    for (let i = visible.length - 1; i >= 0; i--) {
      if (visible[i].speaker === speaker) return visible[i].startedAtMs;
    }
    return 0;
  };

  const activePartial = (speaker: "caller" | "callee"): PartialTranscript | null => {
    const p = partials[speaker];
    if (!p) return null;
    if (p.startedAtMs <= latestCommittedAt(speaker)) return null;
    if (!p.sourceText.trim()) return null;
    return p;
  };

  const callerPartial = activePartial("caller");
  const calleePartial = activePartial("callee");

  useEffect(() => {
    scrollRef.current?.scrollToEnd({ animated: true });
  }, [
    visible.length,
    callerPartial?.sourceText,
    calleePartial?.sourceText,
  ]);

  const hasAny =
    visible.length > 0 || callerPartial !== null || calleePartial !== null;

  return (
    <View style={styles.panel}>
      <View style={styles.handle} />
      <View style={styles.header}>
        <Text style={styles.title}>{title}</Text>
        <Text style={styles.meta}>
          {visible.length} {visible.length === 1 ? "line" : "lines"}
        </Text>
      </View>
      <ScrollView
        ref={scrollRef}
        style={styles.scroll}
        contentContainerStyle={styles.content}
        showsVerticalScrollIndicator={false}
        onContentSizeChange={() =>
          scrollRef.current?.scrollToEnd({ animated: false })
        }
      >
        {!hasAny ? <Text style={styles.empty}>{waitingLabel}</Text> : null}
        {visible.map((seg) => (
          <Bubble
            key={seg.id}
            speaker={seg.speaker}
            youLabel={youLabel}
            themLabel={themLabel}
            languageCode={seg.sourceLanguage.toUpperCase()}
            sourceText={seg.sourceText}
            translatedText={seg.translatedText}
            timestampMs={seg.startedAtMs}
            lowConfidence={lowConfidenceIds.has(seg.id)}
            isPartial={false}
          />
        ))}
        {callerPartial ? (
          <Bubble
            key={`partial-caller-${callerPartial.startedAtMs}`}
            speaker="caller"
            youLabel={youLabel}
            themLabel={themLabel}
            languageCode={callerPartial.sourceLanguage.toUpperCase()}
            sourceText={`${callerPartial.sourceText}…`}
            translatedText=""
            timestampMs={callerPartial.startedAtMs}
            lowConfidence={false}
            isPartial
          />
        ) : null}
        {calleePartial ? (
          <Bubble
            key={`partial-callee-${calleePartial.startedAtMs}`}
            speaker="callee"
            youLabel={youLabel}
            themLabel={themLabel}
            languageCode={calleePartial.sourceLanguage.toUpperCase()}
            sourceText={`${calleePartial.sourceText}…`}
            translatedText=""
            timestampMs={calleePartial.startedAtMs}
            lowConfidence={false}
            isPartial
          />
        ) : null}
      </ScrollView>
    </View>
  );
}

type BubbleProps = {
  speaker: "caller" | "callee";
  youLabel: string;
  themLabel: string;
  languageCode: string;
  sourceText: string;
  translatedText: string;
  timestampMs: number;
  lowConfidence: boolean;
  isPartial: boolean;
};

function Bubble({
  speaker,
  youLabel,
  themLabel,
  languageCode,
  sourceText,
  translatedText,
  timestampMs,
  lowConfidence,
  isPartial,
}: BubbleProps) {
  const isCaller = speaker === "caller";
  const label = isCaller ? youLabel : themLabel;
  return (
    <View style={[styles.row, isCaller ? styles.rowRight : styles.rowLeft]}>
      <View
        style={[
          styles.bubble,
          isCaller ? styles.bubbleCaller : styles.bubbleCallee,
          isPartial ? styles.bubblePartial : null,
        ]}
      >
        <View style={styles.bubbleHeader}>
          <Text
            style={[
              styles.chip,
              isCaller ? styles.chipCaller : styles.chipCallee,
            ]}
          >
            {label} · {languageCode}
          </Text>
          <Text
            style={[
              styles.time,
              isCaller ? styles.timeCaller : styles.timeCallee,
            ]}
          >
            {formatTs(timestampMs)}
          </Text>
        </View>
        {sourceText ? (
          <Text
            style={[
              styles.source,
              isCaller ? styles.sourceCaller : styles.sourceCallee,
              isPartial ? styles.textItalic : null,
            ]}
          >
            {sourceText}
          </Text>
        ) : null}
        {translatedText ? (
          <Text
            style={[
              styles.translated,
              isCaller ? styles.translatedCaller : styles.translatedCallee,
            ]}
          >
            {translatedText}
          </Text>
        ) : null}
        {lowConfidence ? (
          <Text
            style={[
              styles.lowConf,
              isCaller ? styles.lowConfCaller : styles.lowConfCallee,
            ]}
          >
            ⚠ low confidence
          </Text>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  panel: {
    borderRadius: 28,
    backgroundColor: "rgba(247, 242, 235, 0.97)",
    paddingTop: 12,
    paddingHorizontal: 12,
    paddingBottom: 12,
    gap: 10,
  },
  handle: {
    alignSelf: "center",
    width: 42,
    height: 5,
    borderRadius: 999,
    backgroundColor: "#d2c7bb",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 6,
  },
  title: { color: "#101b28", fontSize: 18, fontWeight: "800" },
  meta: { color: "#5b6472", fontSize: 13, fontWeight: "700" },
  scroll: { maxHeight: 260 },
  content: { gap: 8, paddingBottom: 6 },
  empty: {
    color: "#657181",
    fontSize: 14,
    lineHeight: 20,
    textAlign: "center",
    paddingVertical: 16,
  },
  row: { width: "100%", flexDirection: "row" },
  rowRight: { justifyContent: "flex-end" },
  rowLeft: { justifyContent: "flex-start" },
  bubble: {
    maxWidth: "82%",
    borderRadius: 18,
    paddingVertical: 10,
    paddingHorizontal: 14,
    gap: 4,
  },
  bubbleCaller: {
    backgroundColor: "#122033",
    borderBottomRightRadius: 6,
  },
  bubbleCallee: {
    backgroundColor: "#fffdf9",
    borderWidth: 1,
    borderColor: "#ece3d8",
    borderBottomLeftRadius: 6,
  },
  bubblePartial: { opacity: 0.55 },
  bubbleHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },
  chip: {
    fontSize: 10,
    fontWeight: "800",
    textTransform: "uppercase",
    letterSpacing: 0.8,
  },
  chipCaller: { color: "rgba(247, 242, 235, 0.8)" },
  chipCallee: { color: "#677487" },
  time: { fontSize: 10, fontWeight: "700" },
  timeCaller: { color: "rgba(247, 242, 235, 0.6)" },
  timeCallee: { color: "#8a94a5" },
  source: { fontSize: 15, lineHeight: 21, fontWeight: "700" },
  sourceCaller: { color: "#fffdf9" },
  sourceCallee: { color: "#122033" },
  translated: { fontSize: 13, lineHeight: 19 },
  translatedCaller: { color: "rgba(247, 242, 235, 0.78)" },
  translatedCallee: { color: "#4d5c72" },
  textItalic: { fontStyle: "italic" },
  lowConf: { fontSize: 10, fontWeight: "700", marginTop: 2 },
  lowConfCaller: { color: "#ffcf8f" },
  lowConfCallee: { color: "#b86f26" },
});
